'use client'

import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDismissablePopover } from '@kiao-voice/hooks'
import { CalendarDays } from 'lucide-react'
import { DayPicker } from 'react-day-picker'
import { createPortal } from 'react-dom'
import 'react-day-picker/style.css'
import { Button } from './button'
import { cn } from './cn'
import { Input, mergeAriaDescribedBy, mergeAriaLabelledBy, useFieldA11y } from './field'

/**
 * Admin date+time field replacing the bare `<input type="datetime-local">`
 * (whose native popup calendar is browser-chrome blue and clashes with the
 * mono design system). A calendar popover (react-day-picker, the same engine
 * as accounting's shadcn Calendar) styled with KIAO tokens, plus a native
 * time input for the clock part.
 *
 * The `value` contract is IDENTICAL to `<input type="datetime-local">` —
 * `YYYY-MM-DDTHH:mm` in Asia/Taipei wall time, '' when empty — so callers
 * keep using `taipeiInputToIso`/`isoToTaipeiInput` unchanged.
 */

export interface DateFieldProps {
  id?: string
  value: string
  onChange: (value: string) => void
  /** Called immediately before the popover opens, for refreshing dynamic bounds. */
  onOpen?: () => void
  /** Time used after choosing a date while the field is empty. */
  defaultTime?: string
  /** Optional inclusive bounds, same `YYYY-MM-DDTHH:mm` format as `value`. */
  min?: string
  max?: string
  disabled?: boolean
  /** Hide the clock while preserving the `YYYY-MM-DDTHH:mm` value contract. */
  time?: boolean
  /** User-facing copy belongs to the consuming application, not ui-kit. */
  labels?: Partial<DateFieldLabels>
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  'aria-labelledby'?: string
}

export interface DateFieldLabels {
  placeholder: string
  dialogLabel: string
  timeLabel: string
  clear: string
  done: string
}

const DEFAULT_LABELS: DateFieldLabels = {
  placeholder: 'Pick date & time',
  dialogLabel: 'Pick a date and time',
  timeLabel: 'Time',
  clear: 'Clear',
  done: 'Done',
}

interface PopoverPosition {
  top: number
  left: number
  maxHeight: number
  maxWidth: number
}

/** `YYYY-MM-DDTHH:mm` → its date/time halves; null when empty/malformed. */
function splitValue(value: string): { date: string; time: string } | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value)
  return match ? { date: match[1], time: match[2] } : null
}

/** Local calendar date (no TZ conversion — the value is already wall time). */
function toDate(dateStr: string): Date | undefined {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? undefined : date
}

function toDateStr(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function clampToBounds(value: string, min?: string, max?: string): string {
  const normalizedMin = min ? splitValue(min) : null
  const minValue = normalizedMin ? `${normalizedMin.date}T${normalizedMin.time}` : null
  if (minValue && value < minValue) return minValue

  const normalizedMax = max ? splitValue(max) : null
  const maxValue = normalizedMax ? `${normalizedMax.date}T${normalizedMax.time}` : null
  if (maxValue && value > maxValue) return maxValue

  return value
}

export function DateField({
  id,
  value,
  onChange,
  onOpen,
  defaultTime = '23:59',
  min,
  max,
  disabled,
  time = true,
  labels,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
}: DateFieldProps) {
  const { controlId, invalid: fieldInvalid, labelId, descriptionIds } = useFieldA11y()
  const invalid = ariaInvalid ?? (fieldInvalid || undefined)
  const describedBy = mergeAriaDescribedBy(ariaDescribedBy, descriptionIds)
  const text = { ...DEFAULT_LABELS, ...labels }
  const fallbackId = useId()
  const fieldId = id ?? controlId ?? fallbackId
  const labelledBy = mergeAriaLabelledBy(ariaLabelledBy, labelId)
  const panelId = `${fieldId}-popover`
  const timeInputId = `${fieldId}-time`
  const panelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { open, toggle, close, containerRef, triggerRef } = useDismissablePopover<
    HTMLDivElement,
    HTMLButtonElement
  >({ additionalContainerRef: panelRef, preventScrollOnFocusRestore: true })
  const pendingValueRef = useRef(value)
  const wasOpenRef = useRef(open)
  const changedWhileOpenRef = useRef(false)
  const portalHost = open ? (triggerRef.current?.closest('dialog') ?? document.body) : null

  useLayoutEffect(() => {
    pendingValueRef.current = value
  }, [value])

  const emitChange = useCallback(
    (nextValue: string) => {
      pendingValueRef.current = nextValue
      changedWhileOpenRef.current = true
      onChange(nextValue)
    },
    [onChange]
  )

  useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open
    if (!wasOpen || open || !changedWhileOpenRef.current) return

    const candidate = pendingValueRef.current
    if (!candidate) return
    const clamped = clampToBounds(candidate, min, max)
    if (candidate !== clamped) emitChange(clamped)
  }, [open, min, max, emitChange])

  // Body portals use viewport-fixed coordinates. Native dialogs live in the
  // browser's top layer, so their portals use padding-box-relative absolute
  // coordinates inside the dialog instead.
  const [position, setPosition] = useState<PopoverPosition | null>(null)
  useLayoutEffect(() => {
    if (!open || !portalHost) {
      setPosition(null)
      return
    }
    const commitPosition = (next: PopoverPosition) => {
      setPosition((previous) =>
        previous &&
        previous.top === next.top &&
        previous.left === next.left &&
        previous.maxHeight === next.maxHeight &&
        previous.maxWidth === next.maxWidth
          ? previous
          : next
      )
    }
    const isBodyPortal = portalHost === document.body
    const reposition = () => {
      const trigger = triggerRef.current
      const panel = panelRef.current
      const content = contentRef.current
      if (!trigger || !panel || !content) return
      const rect = trigger.getBoundingClientRect()
      const panelStyle = window.getComputedStyle(panel)
      const cssPixels = (value: string) => Number.parseFloat(value) || 0
      const verticalChrome =
        cssPixels(panelStyle.paddingTop) +
        cssPixels(panelStyle.paddingBottom) +
        cssPixels(panelStyle.borderTopWidth) +
        cssPixels(panelStyle.borderBottomWidth)
      const horizontalChrome =
        cssPixels(panelStyle.paddingLeft) +
        cssPixels(panelStyle.paddingRight) +
        cssPixels(panelStyle.borderLeftWidth) +
        cssPixels(panelStyle.borderRightWidth)
      const panelHeight = Math.max(
        panel.offsetHeight,
        Math.max(content.offsetHeight, content.scrollHeight) + verticalChrome
      )
      const panelWidth = Math.max(
        panel.offsetWidth,
        Math.max(content.offsetWidth, content.scrollWidth) + horizontalChrome
      )
      const gap = 6
      const edgeGap = 8
      const hostRect = isBodyPortal
        ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
        : portalHost.getBoundingClientRect()
      const originTop = hostRect.top + (isBodyPortal ? 0 : portalHost.clientTop)
      const originLeft = hostRect.left + (isBodyPortal ? 0 : portalHost.clientLeft)
      const hostHeight =
        !isBodyPortal && portalHost.clientHeight > 0 ? portalHost.clientHeight : hostRect.height
      const hostWidth =
        !isBodyPortal && portalHost.clientWidth > 0 ? portalHost.clientWidth : hostRect.width
      let panelVisibleTop = Math.max(originTop, 0)
      let panelVisibleBottom = Math.min(originTop + hostHeight, window.innerHeight)
      let panelVisibleLeft = Math.max(originLeft, 0)
      let panelVisibleRight = Math.min(originLeft + hostWidth, window.innerWidth)
      const hasMeasurableHostBounds = isBodyPortal || hostWidth > 0 || hostHeight > 0
      const hostIntersectsViewport =
        panelVisibleBottom > panelVisibleTop && panelVisibleRight > panelVisibleLeft
      let triggerVisibleTop = panelVisibleTop
      let triggerVisibleBottom = panelVisibleBottom
      let triggerVisibleLeft = panelVisibleLeft
      let triggerVisibleRight = panelVisibleRight
      const clipsOverflow = (value: string) =>
        value === 'auto' || value === 'scroll' || value === 'hidden' || value === 'clip'
      for (
        let ancestor = trigger.parentElement;
        ancestor && ancestor !== portalHost;
        ancestor = ancestor.parentElement
      ) {
        const ancestorStyle = window.getComputedStyle(ancestor)
        const clipsX = clipsOverflow(ancestorStyle.overflowX || ancestorStyle.overflow)
        const clipsY = clipsOverflow(ancestorStyle.overflowY || ancestorStyle.overflow)
        if (!clipsX && !clipsY) continue

        const ancestorRect = ancestor.getBoundingClientRect()
        if (clipsX) {
          triggerVisibleLeft = Math.max(triggerVisibleLeft, ancestorRect.left)
          triggerVisibleRight = Math.min(triggerVisibleRight, ancestorRect.right)
          if (!isBodyPortal) {
            panelVisibleLeft = triggerVisibleLeft
            panelVisibleRight = triggerVisibleRight
          }
        }
        if (clipsY) {
          triggerVisibleTop = Math.max(triggerVisibleTop, ancestorRect.top)
          triggerVisibleBottom = Math.min(triggerVisibleBottom, ancestorRect.bottom)
          if (!isBodyPortal) {
            panelVisibleTop = triggerVisibleTop
            panelVisibleBottom = triggerVisibleBottom
          }
        }
      }
      const boundaryTop = panelVisibleTop + edgeGap
      const boundaryBottom = panelVisibleBottom - edgeGap
      const boundaryLeft = panelVisibleLeft + edgeGap
      const boundaryRight = panelVisibleRight - edgeGap
      const triggerIntersectsBoundary =
        rect.bottom > triggerVisibleTop &&
        rect.top < triggerVisibleBottom &&
        rect.right > triggerVisibleLeft &&
        rect.left < triggerVisibleRight
      const hasVisibleBoundary =
        panelVisibleBottom > panelVisibleTop && panelVisibleRight > panelVisibleLeft
      const hasMeasurableTriggerRect = rect.width > 0 || rect.height > 0
      if (
        (hasMeasurableHostBounds && !hostIntersectsViewport) ||
        (hasMeasurableTriggerRect && (!hasVisibleBoundary || !triggerIntersectsBoundary))
      ) {
        // A hidden, still-open modal would strand focus inside an invisible
        // panel. Restore focus without scrolling before closing so keyboard
        // navigation remains anchored to the field.
        close(true)
        return
      }
      const belowTop = rect.bottom + gap
      const flippedTop = rect.top - gap - panelHeight
      const fitsBelow = belowTop >= boundaryTop && belowTop + panelHeight <= boundaryBottom
      const fitsAbove = flippedTop >= boundaryTop && flippedTop + panelHeight <= boundaryBottom
      const maxTop = Math.max(boundaryTop, boundaryBottom - panelHeight)
      const top = fitsBelow
        ? belowTop
        : fitsAbove
          ? flippedTop
          : Math.max(boundaryTop, Math.min(belowTop, maxTop))
      const maxLeft = Math.max(boundaryLeft, boundaryRight - panelWidth)
      const left = Math.max(boundaryLeft, Math.min(rect.left, maxLeft))
      const maxHeight = Math.max(0, boundaryBottom - top)
      const maxWidth = Math.max(0, boundaryRight - left)
      if (isBodyPortal) {
        commitPosition({ top, left, maxHeight, maxWidth })
        return
      }

      commitPosition({
        top: top - originTop + portalHost.scrollTop,
        left: left - originLeft + portalHost.scrollLeft,
        maxHeight,
        maxWidth,
      })
    }
    reposition()
    const content = contentRef.current
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !content ? null : new ResizeObserver(reposition)
    if (resizeObserver) {
      const targets = isBodyPortal
        ? [content, triggerRef.current]
        : [content, triggerRef.current, portalHost]
      for (const target of new Set(targets)) {
        if (target) {
          resizeObserver.observe(target)
        }
      }
    }

    // Scrolling the drawer body (or resizing) would strand the panel away from
    // its trigger, so follow it while it remains inside every clipping ancestor:
    // picking a date is a multi-step interaction (month nav → day → time) and
    // incidental scrolls (including automation's scrollIntoView) must not
    // dismiss it mid-flow.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, portalHost, triggerRef, close])

  const parts = splitValue(value)
  const selected = parts ? toDate(parts.date) : undefined
  const minParts = min ? splitValue(min) : null
  const maxParts = max ? splitValue(max) : null
  const minDate = minParts ? toDate(minParts.date) : undefined
  const maxDate = maxParts ? toDate(maxParts.date) : undefined
  const calendarMonth =
    selected && minDate && selected < minDate
      ? minDate
      : selected && maxDate && selected > maxDate
        ? maxDate
        : (selected ?? minDate ?? maxDate)

  const disabledDays = useMemo(() => {
    const rules: Array<{ before: Date } | { after: Date }> = []
    if (minDate) rules.push({ before: minDate })
    if (maxDate) rules.push({ after: maxDate })
    return rules
  }, [minDate, maxDate])

  const display = parts
    ? `${parts.date.replaceAll('-', '/')}${time ? ` ${parts.time}` : ''}`
    : text.placeholder

  const handleSelectDay = useCallback(
    (day: Date | undefined) => {
      if (!day) return
      const time = splitValue(value)?.time ?? defaultTime
      emitChange(clampToBounds(`${toDateStr(day)}T${time}`, min, max))
    },
    [value, defaultTime, min, max, emitChange]
  )

  const handleTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const time = event.target.value
      const date = splitValue(value)?.date
      if (!date || !time) return
      emitChange(clampToBounds(`${date}T${time}`, min, max))
    },
    [value, min, max, emitChange]
  )

  const handleTimeBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!changedWhileOpenRef.current) return
      const time = event.currentTarget.value
      const date = splitValue(value)?.date
      if (!date || !time) return
      const candidate = `${date}T${time}`
      const clamped = clampToBounds(candidate, min, max)
      if (candidate !== clamped) emitChange(clamped)
    },
    [value, min, max, emitChange]
  )

  const handleClear = useCallback(() => {
    emitChange('')
    close(true)
  }, [emitChange, close])

  const handleToggle = useCallback(() => {
    if (!open) {
      changedWhileOpenRef.current = false
      onOpen?.()
    }
    toggle()
  }, [open, onOpen, toggle])

  const handlePanelKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      const style = window.getComputedStyle(element)
      return style.visibility !== 'hidden' && style.display !== 'none'
    })
    const first = focusable[0]
    const last = focusable.at(-1)
    if (!first || !last) return

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }, [])

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        id={fieldId}
        type="button"
        className={cn(
          'flex w-full h-[var(--ctl-h)] items-center justify-between gap-2 rounded-[var(--radius)] border border-border bg-background px-3 text-left font-mono text-[length:var(--text-base)] text-foreground focus:border-foreground focus:outline-2 focus:outline-[var(--fg-soft)]',
          !parts && 'text-muted',
          invalid && 'border-error outline-2 outline-[var(--error-soft)]'
        )}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        onClick={handleToggle}
      >
        <span>{display}</span>
        <CalendarDays aria-hidden="true" className="size-4 shrink-0 text-muted" />
      </button>

      {portalHost
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-modal="true"
              aria-label={text.dialogLabel}
              className={cn(
                portalHost === document.body ? 'fixed' : 'absolute',
                'z-[80] overflow-auto rounded-[10px] border border-border bg-background p-3 shadow-[var(--shadow)]'
              )}
              style={
                position
                  ? {
                      top: position.top,
                      left: position.left,
                      maxHeight: position.maxHeight,
                      maxWidth: position.maxWidth,
                    }
                  : { visibility: 'hidden' }
              }
              onKeyDown={handlePanelKeyDown}
            >
              <div ref={contentRef}>
                <DayPicker
                  autoFocus
                  mode="single"
                  selected={selected}
                  defaultMonth={calendarMonth}
                  onSelect={handleSelectDay}
                  disabled={disabledDays}
                  weekStartsOn={1}
                  classNames={{
                    root: 'rdp-root text-[length:var(--text-sm)]',
                    months: 'relative',
                    month_caption: 'flex h-8 items-center px-1 font-medium',
                    nav: 'absolute right-0 top-0 flex h-8 items-center gap-1',
                    button_previous:
                      'grid h-7 w-7 place-items-center rounded-[6px] text-[var(--muted)] hover:bg-[var(--fg-soft)] hover:text-[var(--fg)]',
                    button_next:
                      'grid h-7 w-7 place-items-center rounded-[6px] text-[var(--muted)] hover:bg-[var(--fg-soft)] hover:text-[var(--fg)]',
                    chevron: 'h-4 w-4 fill-current',
                    weekdays: 'flex',
                    weekday:
                      'w-8 py-1 text-center font-mono text-3xs uppercase tracking-[0.08em] text-[var(--muted)] font-normal',
                    week: 'flex',
                    day: 'p-0',
                    day_button:
                      'grid h-8 w-8 place-items-center rounded-[6px] font-mono text-[length:var(--text-xs)] hover:bg-[var(--fg-soft)] disabled:pointer-events-none disabled:opacity-35',
                    selected: '[&>button]:bg-[var(--fg)] [&>button]:text-[var(--bg)]',
                    today: 'font-semibold',
                    outside: 'opacity-35',
                    hidden: 'invisible',
                  }}
                />
                <div className="sticky bottom-0 left-0 mt-2 flex items-center justify-between gap-3 border-t border-[var(--border)] bg-[var(--bg)] pt-2.5">
                  {time ? (
                    <Input
                      type="time"
                      // Input falls back to the surrounding Field's controlId,
                      // which the trigger button already owns — two nodes would
                      // answer to the same ID and break `label[for]` and every
                      // aria reference pointing at it.
                      id={timeInputId}
                      className="h-8 w-auto px-2 font-mono text-[length:var(--text-xs)]"
                      aria-label={text.timeLabel}
                      value={parts?.time ?? ''}
                      min={parts && minParts?.date === parts.date ? minParts.time : undefined}
                      max={parts && maxParts?.date === parts.date ? maxParts.time : undefined}
                      disabled={!parts}
                      onChange={handleTimeChange}
                      onBlur={handleTimeBlur}
                    />
                  ) : (
                    <span />
                  )}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 py-1 text-[length:var(--text-xs)]"
                      onClick={handleClear}
                    >
                      {text.clear}
                    </Button>
                    <Button size="sm" onClick={() => close(true)}>
                      {text.done}
                    </Button>
                  </div>
                </div>
              </div>
            </div>,
            portalHost
          )
        : null}
    </div>
  )
}
